import React, { useEffect, useMemo, useState } from 'react';
import {
	View,
	TouchableOpacity,
	FlatList,
	StyleSheet,
	useWindowDimensions
} from 'react-native';
import { ZenEmptyComponent, ZenIcon, ZenLoader, ZenText, ZenToast } from 'zen-core-elements';
import SellscreenModal from '../sellscreenModal';
import { getAllEligibleDiscountPayload, overAllDiscount } from '../../sellScreen/sellScreenModule';
import { config } from '../../../shared/environment';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { Theme, ThemeConsumer } from '@rneui/themed';
import { CustomSearchBar } from '../customSearchBar';
import { addManualDiscount, getAllEligibleDiscount } from '../../../redux/slices/newSellscreenSlice';
import { validateManualDiscount } from '../../../redux/utils/discountUtils';
import { showApiError } from '../../../shared/utils/handleApiError';
import { webDialogWidth } from '../../../platform/design/dialogWeb';

const CouponModal = ({ visible, onClose }: { visible: boolean, onClose: () => void }) => {
	const dispatch = useAppDispatch()
	const { id: locationId } = useAppSelector((state) => state?.common?.currentLocation)
	const { getAllEligibleDiscount: couponDiscounts, cartTotal, cartQuantity, manualDiscount, customerDetails } = useAppSelector((state) => state.NewSellscreenSlice)
	const [inputCode, setInputCode] = useState<string | null>(null);
	const { width, height } = useWindowDimensions();
	const [loader, setLoader] = useState(false)

	useEffect(() => {
		if (visible)
			getAllCouponDiscount()
	}, [visible])

	const getAllCouponDiscount = async () => {
		if (!couponDiscounts?.length) {
			setLoader(true)
		}
		let payload: getAllEligibleDiscountPayload = {
			isPos: true,
			currentDate: new Date().toISOString(),
			customerId: customerDetails?.customerId || null,
			storeId: config?.storeId,
			amount: {
				orderAmount: cartTotal[locationId],
				productQty: cartQuantity[locationId],
			},
			locationId
		}
		await dispatch(getAllEligibleDiscount({ storeId: config?.storeId, payload })).unwrap().catch(showApiError).finally(() => {
			setLoader(false)
		})
	}

	const handleApplyCoupon = (item: overAllDiscount) => {
		const validation = validateManualDiscount({
			discount: item,
			cartTotal: cartTotal[locationId],
			cartQuantity: cartQuantity[locationId],
			customerId: customerDetails?.customerId || null,
		});
		if (validation.isApplicable) {
			dispatch(addManualDiscount({ discount: item, locationId }))
			onClose()
		} else {
			ZenToast({ type: "warning", message: validation.message || "Invalid discount" });
		}
	}

	const removeCoupon = () => {
		dispatch(addManualDiscount({ discount: null, locationId }))
	}

	const renderCoupon = ({ item }: { item: overAllDiscount }) => {
		const isSelected = item.id === manualDiscount?.[locationId]?.id
		return (
			<ThemeConsumer>
				{({ theme }) => {
					const styles = style(theme)
					return (
						<TouchableOpacity style={[styles.couponCard, isSelected && styles.couponCardSelected]} onPress={() => isSelected ? removeCoupon() : handleApplyCoupon(item)}>
							<View style={styles.couponLeft}>
								<View style={styles.iconCircle}>
									<ZenIcon name='ticket' type='entypo' color={theme?.primary} size={20} />
								</View>
								<View style={styles.textContainer}>
									<ZenText content={item.promoCode} style={styles.couponCodeText} />
									<ZenText content={item.offerValue} style={styles.discountText} />
									<ZenText content={item.offerDescription} style={styles.descriptionText} />
								</View>
							</View>
							{isSelected ?
								<TouchableOpacity style={[styles.applySmallBtn, { backgroundColor: theme?.red500 }]} onPress={removeCoupon}>
									<ZenText content="Remove" style={[styles.applySmallText, { color: theme?.light }]} />
								</TouchableOpacity>
								:
								<TouchableOpacity style={styles.applySmallBtn} onPress={() => handleApplyCoupon(item)}>
									<ZenText content="Apply" style={styles.applySmallText} />
								</TouchableOpacity>
							}
						</TouchableOpacity>
					)
				}}
			</ThemeConsumer>
		)
	}

	const searchedCoupon = useMemo(() => {
		if (inputCode)
			return couponDiscounts?.filter((item) => item.promoCode?.toLowerCase()?.includes(inputCode?.toLowerCase()))
		else
			return couponDiscounts
	}, [couponDiscounts, inputCode])

	return (
		<ThemeConsumer>
			{({ theme }) => {
				const styles = style(theme)
				return (
					<SellscreenModal isModalOpen={visible} setModalOpen={onClose}>
						<View style={[{ minWidth: width > 900 ? width * 0.5 : width > 600 ? width * 0.60 : width * 0.80, maxHeight: width > 900 ? height * 0.60 : height * 0.40, }, webDialogWidth('lg', width)]}>
							{/* Header */}
							<View style={styles.header}>
								<ZenText content="Coupon Discount" style={styles.headerTitle} />
								<TouchableOpacity onPress={onClose}>
									<ZenIcon name='close' type='antdesign' color={theme?.lightDark} size={24} />
								</TouchableOpacity>
							</View>

							{/* Input Area */}
							<View style={{ paddingVertical: 20 }}>
								<CustomSearchBar
									placeholder='Enter a coupon code'
									search={inputCode}
									setSearch={setInputCode}
									toggleOffset={() => { }}
									backgroundColor={theme?.iceWhite}
									leftIcon={false}
								// rightComponent={<TouchableOpacity
								// 	style={[styles.applyBtn, !inputCode && styles.applyBtnDisabled]}
								// 	disabled={!inputCode}
								// >
								// 	<ZenText content="Apply" style={[styles.applyBtnText, !inputCode && { color: theme?.disableTextColor }]} />
								// </TouchableOpacity>}
								/>
							</View>

							<ZenText content="Available Coupons" style={styles.sectionTitle} />

							{loader ?
								<ZenLoader viewStyle={{ backgroundColor: "transparent", flex: 1 }} color={theme?.primary} lottieType="Local" lottie={require('../../../assets/infinityLoader.json')} />
								:
								<FlatList
									data={searchedCoupon}
									renderItem={renderCoupon}
									keyExtractor={(item) => item.id?.toString()}
									showsVerticalScrollIndicator={false}
									ListEmptyComponent={
										<ZenEmptyComponent text="No Coupons Found" viewStyle={{ marginTop: 30 }} />
									}
								/>}
						</View>
					</SellscreenModal>
				)
			}}
		</ThemeConsumer>
	);
};

const style = (theme: Theme) =>
	StyleSheet.create({
		header: {
			flexDirection: 'row',
			justifyContent: 'space-between',
			alignItems: 'center',
			paddingBottom: 16,
			borderBottomWidth: 1,
			borderBottomColor: theme?.softSlate,
		},
		headerTitle: {
			fontSize: theme?.mediumFontSize,
			fontFamily: theme?.bold,
			color: theme?.lightDark,
		},
		applyBtn: {
			backgroundColor: theme?.primary,
			justifyContent: 'center',
			paddingHorizontal: 20,
			paddingVertical: 12,
			borderRadius: 12,
		},
		applyBtnDisabled: {
			backgroundColor: theme?.disableGray,
		},
		applyBtnText: {
			fontSize: theme?.smallHeaderFontSize,
			color: theme?.light,
		},
		sectionTitle: {
			fontSize: theme?.smallHeaderFontSize,
			fontFamily: theme?.medium,
			color: theme?.grey,
			paddingHorizontal: 18,
			marginBottom: 12,
			textTransform: 'uppercase',
			letterSpacing: 0.5,
		},
		couponCard: {
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'space-between',
			backgroundColor: theme?.light,
			borderWidth: 1,
			borderColor: theme?.coolGray,
			borderRadius: 16,
			padding: 16,
			marginBottom: 12,
			borderStyle: 'dashed'
		},
		couponCardSelected: {
			borderStyle: "solid",
			borderColor: theme?.primary,
			backgroundColor: theme?.softSlate
		},
		couponLeft: {
			flexDirection: 'row',
			alignItems: 'center',
			flex: 1,
		},
		iconCircle: {
			width: 40,
			height: 40,
			borderRadius: 20,
			backgroundColor: theme?.tealPrimary,
			justifyContent: 'center',
			alignItems: 'center',
			marginRight: 12,
		},
		textContainer: {
			flex: 1,
		},
		couponCodeText: {
			fontSize: theme?.smallerFontSize,
			fontFamily: theme?.bold,
			color: theme?.primary,
			marginBottom: 2,
		},
		discountText: {
			fontSize: theme?.mediumFontSize,
			color: theme?.lightDark,
		},
		descriptionText: {
			fontSize: theme?.miniFontSize,
			color: theme?.grey,
			marginTop: 2,
		},
		applySmallBtn: {
			paddingVertical: 8,
			paddingHorizontal: 12,
			backgroundColor: theme?.softSlate,
			borderRadius: 8,
		},
		applySmallText: {
			fontSize: theme?.smallHeaderFontSize,
			color: theme?.primary,
		},
	});

export default CouponModal;